import { request } from '@umijs/max';
import { registerWsHandler, WsEventType, onSocketReconnected } from '../websocket';

// ─── SMS Center live-update bus (mirrors the call-queue WS pattern in call-queue.ts) ──
// Unlike the call queue (a short, bounded snapshot broadcast wholesale on every push),
// the contact list here is paginated/searchable AND its sort order depends on
// server-side unread state the push doesn't carry — so the payload carries only the
// one message that changed, and SmsBoard always re-fetches the contact list over REST
// on this event rather than patching/reordering it from the payload. Only the open
// thread gets a local append (a plain chronological insert with no ordering to get
// wrong) using `message` below.
export type SmsThreadMessage = {
  id: number | string;
  body?: string;
  timestamp?: string;
  direction: 'inbound' | 'outbound';
  type: 'sms';
  sender_name?: string;
  from_number: string;
  to_number: string;
  attachments?: any[];
  // Twilio delivery status (queued/sent/delivered/undelivered/failed). Present on the
  // initial send push and on later status-only pushes (see handleTwilioSMSStatusCallback),
  // which carry just this field for an id the client already has — merge, don't replace.
  delivery_status?: string;
};

export type SmsThreadUpdatedPayload = {
  phone_number: string;
  message: SmsThreadMessage;
};

const SMS_THREAD_UPDATED_EVENT = 'smsThreadUpdated';

export function emitSmsThreadUpdated(data?: SmsThreadUpdatedPayload) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<SmsThreadUpdatedPayload | undefined>(SMS_THREAD_UPDATED_EVENT, { detail: data }));
}

/** Subscribe to SMS pushes; returns an unsubscribe function. `data` is undefined for a
 * signal-only push (socket reconnect, or an older server) — the listener should re-fetch. */
export function onSmsThreadUpdated(handler: (data?: SmsThreadUpdatedPayload) => void) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<SmsThreadUpdatedPayload | undefined>).detail);
  window.addEventListener(SMS_THREAD_UPDATED_EVENT, listener);
  return () => window.removeEventListener(SMS_THREAD_UPDATED_EVENT, listener);
}

registerWsHandler(WsEventType.SMS_THREAD_UPDATED, emitSmsThreadUpdated);

// A dropped push during a disconnect never arrives on its own — resync the instant the
// socket comes back instead of leaving the board stale (there's no poll fallback for SMS
// Center any more, so this is the only resync path besides the user hitting Refresh).
onSocketReconnected(() => emitSmsThreadUpdated());

export async function getTenantContactsPaginated(params?: {
  page?: number;
  limit?: number;
  filter?: string;
  search?: string;
  job_status_filter?: string;
  skip_assigned_check?: ZeroOrOne;
  customer_only?: ZeroOrOne;
  /** Only contacts with unread SMS — the bell's SMS list. */
  unread_only?: ZeroOrOne;
}) {
  return request('/api/tenants/tenant_contacts_paginated', {
    method: 'GET',
    params,
  });
}

/** Contact totals per Callboard tab ({ all, sms, call, email }) in one request — the same
 * numbers `pagination.total` of getTenantContactsPaginated gives per filter, without a search. */
export async function getTenantContactCounts(params?: { skip_assigned_check?: ZeroOrOne }) {
  return request('/api/tenants/tenant_contact_counts', {
    method: 'GET',
    params,
  });
}

export async function getTenantConversation(phone: string, extraParams?: any) {
  return request('/api/tenants/tenant_conversation', {
    method: 'GET',
    params: { phone, ...extraParams, },
  });
}

export async function markTenantConversationRead(phone: string) {
  return request('/api/tenants/tenant_conversation/mark-read', {
    method: 'POST',
    data: { phone },
  });
}

export async function getTechnicianCommHistory(dispatchId: number, params?: { page?: number; limit?: number }) {
  return request(`/api/tenants/dispatch/${dispatchId}/technician-comm-history`, {
    method: 'GET',
    params,
  });
}

export async function getTenantConversationPaginated(params: {
  phone: string;
  page?: number;
  limit?: number;
  tenant_view?: number;
}) {
  return request('/api/tenants/tenant_conversation', {
    method: 'GET',
    params,
  });
}

export async function sendTenantMessage(data: { phone: string; message: string; from_number?: string; files?: File[]; templateMediaUrls?: string[]; templateId?: number }) {
  if ((data.files && data.files.length > 0) || (data.templateMediaUrls && data.templateMediaUrls.length > 0)) {
    const formData = new FormData();
    formData.append('phone', data.phone);
    formData.append('message', data.message || '');
    if (data.from_number) formData.append('from_number', data.from_number);
    if (data.templateId) formData.append('template_id', String(data.templateId));
    data.files?.forEach((file) => formData.append('attachments[]', file));
    data.templateMediaUrls?.forEach((url) => formData.append('template_media_urls[]', url));
    return request('/api/tenants/send_message', {
      method: 'POST',
      data: formData,
    });
  }
  return request('/api/tenants/send_message', {
    method: 'POST',
    data: { phone: data.phone, message: data.message, from_number: data.from_number, template_id: data.templateId },
  });
}

export async function getSmsKeywords() {
  return request<{ data: Array<{ keyword: string; description: string }> }>(
    '/api/sms/keywords',
    { method: 'GET' },
  );
}

export async function getCsrNumbers() {
  return request<{ data: Array<{ id: number; phone_number: string; is_csr: number; assignee_name: string | null }> }>(
    '/api/tenant/csr/numbers',
    { method: 'GET' },
  );
}

// Search customer by phone number
export async function searchCustomerByPhone(phone: string) {
  return request('/api/twilio/tenants/search_by_phone', {
    method: 'POST',
    data: { phone },
  });
}

// Save call notes
export async function saveCallNotes(data: { job_id: string; call_notes: string }) {
  return request('/api/jobs/tenants/update_job_meta', {
    method: 'POST',
    data: {
      job_id: data.job_id,
      meta_key: 'call_notes',
      meta_value: data.call_notes,
    },
  });
}

// Update appointment status
export async function updateAppointmentStatus(data: { job_id: string; appointment_status: number }) {
  return request('/api/jobs/update_job_status', {
    method: 'POST',
    data: {
      job_id: data.job_id,
      data: {
        appointment_status: data.appointment_status,
      },
    },
  });
}

// Get customer jobs for the SMS Center Job History panel.
// Only genuinely-archived jobs are excluded here (Assigned-status and On Hold
// jobs are always shown) — see JobsApi::getCustomerJobsForSmsCenter().
export async function getCustomerJobsForSmsCenter(customerId: number, withParam?: string) {
  return request<API.ResultObject<API.PaginatedResult<API.Dispatch>>>('/api/jobs/get_customer_jobs_for_sms_center', {
    method: 'GET',
    params: { customer_id: customerId, perPage: 5000, ...(withParam ? { with: withParam } : {}) },
  });
}

// Get most recent jobs for a customer (used by call widget caller info popup)
export async function getCustomerRecentJobs(customerId: number) {
  return request<API.ResultObject<API.PaginatedResult<API.Dispatch>>>('/api/jobs/get_customer_jobs', {
    method: 'GET',
    params: { customer_id: customerId, perPage: 3, page: 1, with: 'address,priorityTerm,tags,serviceTypes,technicians' },
  }).then((res) => ({
        data: res.data?.rows ?? [],
        success: res.success ?? false,
        total: res.data?.pagination?.total_records ?? 0,
        pagination: res.data?.pagination,
        total_row: res.data?.total_row,
    }));
}

// Email functions
export async function sendTenantEmail(data: { to: string; subject: string; message: string }) {
  return request('/api/tenants/send_email', {
    method: 'POST',
    data,
  });
}

export async function getTenantEmailConversation(email: string) {
  return request('/api/tenants/email_conversation', {
    method: 'GET',
    params: { email },
  });
}
